import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

// ─────────────────────────────────────────────────────────────────────────────
//  Entitlements + Offerings — single source of truth on the client.
//
//  Backend contract:
//    GET /api/families/{id}/entitlements   → { tier, tier_label, month,
//                                              ai_turns, quick_add, members,
//                                              storage, features }
//    GET /api/billing/offerings            → { tiers: [...], topups, ... }
//
//  Components NEVER read `family.subscription.tier` directly. They go through:
//    const { has, limit, currentTier } = useEntitlement("photos.smart_albums")
//
//  Refresh triggers: app foreground >5min, IAP success, manual `refresh()`.
// ─────────────────────────────────────────────────────────────────────────────

const Ctx = createContext(null);

// Ordering mirrors backend registry.TIER_ORDER. Kept here so the frontend can
// compute "is this feature available on a higher tier than mine" without
// another round-trip. Update in lock-step with backend.
const TIER_ORDER = ["free", "spark", "lite", "plus", "family"];

export function EntitlementsProvider({ children }) {
  const { family } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [offerings, setOfferings] = useState(null);

  const refresh = useCallback(async () => {
    if (!family?.id) { setSnapshot(null); return; }
    try {
      const { data } = await api.get(`/families/${family.id}/entitlements`);
      setSnapshot(data);
    } catch {
      setSnapshot(null);
    }
  }, [family?.id]);

  const refreshOfferings = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/offerings");
      setOfferings(data);
    } catch {
      setOfferings(null);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { refreshOfferings(); }, [refreshOfferings]);

  // Re-fetch on app foreground if backgrounded for >5 min — covers the case
  // where a webhook arrived while the app was suspended.
  useEffect(() => {
    let hiddenAt = null;
    const onVis = () => {
      if (document.hidden) { hiddenAt = Date.now(); return; }
      if (hiddenAt && Date.now() - hiddenAt > 5 * 60 * 1000) refresh();
      hiddenAt = null;
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refresh]);

  const value = useMemo(() => ({
    snapshot,
    offerings,
    refresh,
    refreshOfferings,
  }), [snapshot, offerings, refresh, refreshOfferings]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  useEntitlement(featureKey)
//
//  Returns everything a component needs to make gating + paywall decisions
//  WITHOUT ever referencing a tier name in the component.
//
//    has                  bool  — does my tier grant this?
//    limit                raw   — numeric quota / string mode / list / bool
//    currentTier          str   — "free" / "spark" / ...
//    currentTierLabel     str   — "Free" / "Spark" / ...
//    requiredTier         str?  — minimum tier that grants this feature
//    requiredTierLabel    str?  — friendly label for the upgrade pitch
//    isUnlimited          bool  — `limit === -1`
// ─────────────────────────────────────────────────────────────────────────────
export function useEntitlement(featureKey) {
  const ctx = useContext(Ctx);
  const snap = ctx?.snapshot;
  const offerings = ctx?.offerings;

  return useMemo(() => {
    const currentTier = snap?.tier || "free";
    const currentTierLabel = snap?.tier_label || "Free";
    const featureValue = snap?.features?.[featureKey];

    // `has` mirrors backend resolver.has(): truthy non-disabled value.
    const has = (() => {
      if (featureValue === undefined || featureValue === null) return false;
      if (featureValue === false || featureValue === 0 || featureValue === "" || featureValue === "none") return false;
      if (Array.isArray(featureValue) && featureValue.length === 0) return false;
      return true;
    })();

    // Walk tiers from cheapest → highest to find the minimum tier that grants
    // this feature. Mirrors backend registry.min_tier_for(). We rely on the
    // backend pre-resolving features for the CURRENT tier only, but the
    // offerings catalogue carries the highlights so we can show context.
    let requiredTier = null;
    let requiredTierLabel = null;
    if (!has && offerings?.tiers) {
      const idx = TIER_ORDER.indexOf(currentTier);
      for (let i = idx + 1; i < TIER_ORDER.length; i++) {
        const t = TIER_ORDER[i];
        const tierMeta = offerings.tiers.find(x => x.key === t);
        if (tierMeta) {
          requiredTier = t;
          requiredTierLabel = tierMeta.label;
          break;
        }
      }
    }

    return {
      has,
      limit: featureValue,
      currentTier,
      currentTierLabel,
      requiredTier,
      requiredTierLabel,
      isUnlimited: featureValue === -1,
    };
  }, [snap, offerings, featureKey]);
}

// Hooks for usage meters + raw access — used by Subscription screen.
export function useEntitlementsSnapshot() {
  const ctx = useContext(Ctx);
  return ctx?.snapshot || null;
}

export function useOfferings() {
  const ctx = useContext(Ctx);
  return ctx?.offerings || null;
}

export function useEntitlementsRefresh() {
  const ctx = useContext(Ctx);
  return ctx?.refresh || (() => {});
}
